import React, { useEffect, useState } from 'react';
import { alertsService } from '../services/api';
import { 
  Bell, Loader, CheckCircle, Info, 
  AlertTriangle, XCircle, RefreshCw, Trash2, CheckCheck,
  DollarSign, Zap, TrendingUp, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
import { useCachedApi } from '../hooks/useCachedApi';
// Format de date manuel (pas besoin de date-fns)
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} à ${hours}:${minutes}`;
};


const Alertes = () => {
  const { hasPermission } = usePermissions();
  const [filter, setFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [page, setPage] = useState(1);
  
  const [checking, setChecking] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const PAGE_SIZE = 30;

    // ✅ DONNÉES AVEC CACHE
  const { data: alertsData, loading, refresh: refreshAlerts } = useCachedApi(
    () => alertsService.getAll({
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      unread_only: filter === 'unread' ? true : undefined,
      severity: severityFilter !== 'all' ? severityFilter : undefined
    }),
    [filter, severityFilter, page, refreshKey],
    { cacheDuration: 30000, cacheKey: `alerts-${page}-${filter}-${severityFilter}` }
  );

  const alerts = Array.isArray(alertsData) ? alertsData : (alertsData?.items || []);

  const { data: unreadData } = useCachedApi(
    () => alertsService.getUnreadCount(),
    [refreshKey],
    { cacheDuration: 30000, cacheKey: 'alerts-unread-count' }
  );

  const unreadCount = unreadData?.unread_count || 0;

  const { data: totalData } = useCachedApi(
    () => alertsService.getCount({
      unread_only: filter === 'unread' ? true : undefined,
      severity: severityFilter !== 'all' ? severityFilter : undefined
    }),
    [filter, severityFilter, refreshKey],
    { cacheDuration: 30000, cacheKey: `alerts-count-${filter}-${severityFilter}` }
  );

  const totalAlerts = totalData?.total || 0;
  const totalPages = Math.ceil(totalAlerts / PAGE_SIZE);

  // ✅ Actions
  const markAsRead = async (id) => {
    if (!hasPermission(PERMISSIONS.MARK_ALERT_READ)) {
      toast.error('Permission refusée');
      return;
    }
    try {
      await alertsService.markAsRead(id);
      toast.success('Alerte marquée comme lue');
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      toast.error('Erreur lors du marquage');
    }
  };

  const markAllAsRead = async () => {
    try {
      await alertsService.markAllAsRead();
      toast.success('Toutes les alertes marquées comme lues');
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      toast.error('Erreur lors du marquage');
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Supprimer cette alerte ?')) return;
    try {
      await alertsService.delete(id);
      toast.success('Alerte supprimée');
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const checkAllAlerts = async () => {
    setChecking(true);
    try {
      const res = await alertsService.checkAll();
      const data = res.data;
      
      if (data.total > 0) {
        toast.success(`${data.total} nouvelle(s) alerte(s) détectée(s)`);
      } else {
        toast.info('Aucune nouvelle alerte');
      }
      
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setChecking(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'info': return <Info className="w-5 h-5 text-blue-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      error: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      success: 'bg-green-100 text-green-700 border-green-200',
      info: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return badges[severity] || badges.info;
  };

  // const getAlertTypeLabel = (type) => {
  //   const labels = {
  //     inactive_meter: 'Compteur Inactif',
  //     low_credit: 'Crédit Faible',
  //     tamper: 'Fraude',
  //     blocked_meter: 'Compteur Bloqué',
  //     high_consumption: 'Consommation Élevée',
  //     payment_failed: 'Paiement Échoué',
  //     transaction_success: 'Transaction Réussie',
  //     system: 'Système',
  //     risk_score_change: 'Score de risque modifié' // ✅ AJOUT
  //   };
  //   return labels[type] || type;
  // };
  const getAlertTypeLabel = (type) => {
  const labels = {
    inactive_meter: 'Compteur Inactif',
    low_credit: 'Crédit Faible',
    tamper: 'Fraude détectée',
    blocked_meter: 'Compteur Bloqué',
    automatic_block: 'Blocage Automatique', // ✅
    meter_unblocked: 'Compteur Débloqué',   // ✅
    high_consumption: 'Consommation Élevée',
    payment_failed: 'Paiement Échoué',
    pending_transaction: 'Transaction en attente', // ✅
    transaction_success: 'Transaction Réussie',
    system: 'Système',
    risk_score_change: 'Score de risque modifié'
  };
  return labels[type] || type;
};

const getAlertTypeColor = (type) => {
  const colors = {
    inactive_meter: 'text-orange-600',
    low_credit: 'text-yellow-600',
    tamper: 'text-red-600',
    blocked_meter: 'text-red-700',
    automatic_block: 'text-red-800', // ✅
    meter_unblocked: 'text-green-700', // ✅
    high_consumption: 'text-purple-600',
    payment_failed: 'text-red-600',
    pending_transaction: 'text-blue-600', // ✅
    transaction_success: 'text-green-600',
    system: 'text-blue-600',
    risk_score_change: 'text-indigo-600'
  };
  return colors[type] || 'text-gray-600';
};

  const getAlertTypeIcon = (type) => {
  const icons = {
    transaction_success: <DollarSign className="w-4 h-4" />,
    pending_transaction: <RefreshCw className="w-4 h-4" />, // ✅
    low_credit: <TrendingUp className="w-4 h-4" />,
    high_consumption: <Zap className="w-4 h-4" />,
    automatic_block: <XCircle className="w-4 h-4" />, // ✅
    meter_unblocked: <CheckCircle className="w-4 h-4" /> // ✅
  };
  return icons[type] || null;
};


  // ✅ NOUVEAU: Fonction pour changer de page
  const goToPage = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setPage(pageNumber);
  };

  // ✅ NOUVEAU: Générer les numéros de pages à afficher
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5; // Nombre max de boutons visibles
    
    if (totalPages <= maxVisible) {
      // Afficher toutes les pages si peu nombreuses
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Logique pour afficher avec "..."
      if (page <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Bell className="w-7 h-7" />
            Alertes et Notifications
            {unreadCount > 0 && (
              <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full animate-pulse">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Suivi en temps réel des événements importants ({totalAlerts} alerte{totalAlerts > 1 ? 's' : ''})
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={checkAllAlerts}
            disabled={checking}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
            Vérifier
          </button>
          {unreadCount > 0 && (
            <PermissionGuard permission={PERMISSIONS.MARK_ALERT_READ}>
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                Tout lire
              </button>
            </PermissionGuard>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-2 block">État</label>
            <div className="flex gap-2">
              <button
                onClick={() => { setFilter('all'); setPage(1); }}
                className={`px-4 py-2 rounded-lg transition-colors ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Toutes
              </button>
              <button
                onClick={() => { setFilter('unread'); setPage(1); }}
                className={`px-4 py-2 rounded-lg transition-colors ${filter === 'unread' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Non lues {unreadCount > 0 && `(${unreadCount})`}
              </button>
            </div>
          </div>

          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Gravité</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setSeverityFilter('all'); setPage(1); }}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${severityFilter === 'all' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Toutes
              </button>
              <button
                onClick={() => { setSeverityFilter('success'); setPage(1); }}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${severityFilter === 'success' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
              >
                Succès
              </button>
              <button
                onClick={() => { setSeverityFilter('info'); setPage(1); }}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${severityFilter === 'info' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
              >
                Info
              </button>
              <button
                onClick={() => { setSeverityFilter('warning'); setPage(1); }}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${severityFilter === 'warning' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
              >
                Avertissement
              </button>
              <button
                onClick={() => { setSeverityFilter('error'); setPage(1); }}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${severityFilter === 'error' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
              >
                Erreur
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des alertes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune alerte</h3>
            <p className="text-gray-600">Vous n'avez pas d'alertes pour le moment.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {alerts.map((alert) => (
              <div 
                key={alert.id} 
               className={`p-4 hover:bg-gray-50 transition-colors border-l-4 ${
                !alert.is_read
                  ? alert.severity === 'success'
                    ? 'bg-green-50 border-l-green-500'
                    : 'bg-blue-50 border-l-blue-500'
                  : 'border-l-transparent'
              }`}

              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getSeverityIcon(alert.severity)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityBadge(alert.severity)}`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 ${getAlertTypeColor(alert.alert_type)}`}>
                        {getAlertTypeIcon(alert.alert_type)}
                        {getAlertTypeLabel(alert.alert_type)}
                      </span>
                      {!alert.is_read && (
                        <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                          <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                          Nouveau
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-900 mb-3 leading-relaxed whitespace-pre-line">
                      {alert.message}
                    </p>

                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-xs text-gray-500 flex items-center gap-2">
                        <span>{formatDate(alert.created_at)}</span>
                        {alert.meter_id && (
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                            Compteur: {alert.meter_id.slice(0, 8)}
                          </span>
                        )}
                      </p>

                      <div className="flex items-center gap-2">
                        {!alert.is_read && (
                          <PermissionGuard permission={PERMISSIONS.MARK_ALERT_READ}>
                            <button 
                              onClick={() => markAsRead(alert.id)} 
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium px-3 py-1 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                            >
                              Marquer comme lu
                            </button>
                          </PermissionGuard>
                        )}
                        <button 
                          onClick={() => deleteAlert(alert.id)} 
                          className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ NOUVELLE PAGINATION AMÉLIORÉE */}
      {!loading && totalAlerts > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Info pagination */}
            <div className="text-sm text-gray-600">
              Page <span className="font-semibold">{page}</span> sur <span className="font-semibold">{totalPages}</span>
              <span className="ml-2 text-gray-500">
                ({((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, totalAlerts)} sur {totalAlerts})
              </span>
            </div>

            {/* Boutons de navigation */}
            <div className="flex items-center gap-2">
              {/* Première page */}
              <button
                onClick={() => goToPage(1)}
                disabled={page === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Première page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              {/* Page précédente */}
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Page précédente"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Numéros de pages */}
              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers().map((pageNum, idx) => (
                  pageNum === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`min-w-[40px] px-3 py-2 rounded-lg transition-colors ${
                        page === pageNum
                          ? 'bg-blue-600 text-white font-semibold'
                          : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                ))}
              </div>

              {/* Page suivante */}
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Page suivante"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Dernière page */}
              <button
                onClick={() => goToPage(totalPages)}
                disabled={page === totalPages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Dernière page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Navigation mobile simplifiée */}
          <div className="sm:hidden flex items-center justify-center gap-2 mt-3">
            <button
              onClick={() => goToPage(1)}
              disabled={page === 1}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              Première
            </button>
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              Préc.
            </button>
            <span className="px-3 py-2 text-sm font-semibold">
              {page}/{totalPages}
            </span>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              Suiv.
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={page === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              Dernière
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alertes;